import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { hasFunctionAccess } from "../permissionConfig";
import { API_BASE } from "../config";

function svg(size, children, extra = {}) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...extra}>
            {children}
        </svg>
    );
}

/* Empty/reserved state — matches Landing.jsx's original static "sfip-kpi" placeholder. */
function EmptyKpi() {
    return (
        <div className="sfip-kpi">
            <div className="sfip-kpi-icon">
                {svg(32, (
                    <>
                        <line x1="18" y1="20" x2="18" y2="10" />
                        <line x1="12" y1="20" x2="12" y2="4" />
                        <line x1="6" y1="20" x2="6" y2="14" />
                    </>
                ), { strokeWidth: 1.6 })}
            </div>
            <div className="sfip-kpi-title">KPIs</div>
            <div className="sfip-kpi-text">No KPIs yet — this space is reserved for key performance metrics.</div>
        </div>
    );
}

export default function KpiColumn({ user }) {
    const navigate = useNavigate();
    const canSeeSO = hasFunctionAccess(user, 'po_so_lifecycle', 'so_kam_review');
    const [count, setCount] = useState(null);

    useEffect(() => {
        if (!canSeeSO) return;
        let cancelled = false;
        (async () => {
            try {
                const identityQS = `&username=${encodeURIComponent(user?.username || '')}&role=${encodeURIComponent(user?.role || '')}`;
                const res = await fetch(`${API_BASE}/so-kam-review?action=pending&kam=${identityQS}`);
                const list = await res.json();
                if (!cancelled) setCount(Array.isArray(list) ? list.length : 0);
            } catch (e) {
                console.error('Failed to load SO KPI count', e);
                if (!cancelled) setCount(0);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSeeSO]);

    if (!canSeeSO || count === null) return <EmptyKpi />;

    return (
        <div style={S.column}>
            <div style={S.card} onClick={() => navigate('/po-so-lifecycle/so-kam-review')}>
                <span style={S.attentionPill}>
                    {svg(9, (
                        <>
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                        </>
                    ), { viewBox: "0 0 24 24", stroke: "#F6D3DB", strokeWidth: "2.5" })}
                    ATTENTION
                </span>

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                    <div style={S.iconBadge}>
                        {svg(14, (
                            <>
                                <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                            </>
                        ), { stroke: "#fff", strokeWidth: "1.8" })}
                    </div>
                    <span style={S.metricLabel}>SALES ORDERS</span>
                </div>

                <div>
                    <div style={S.value}>{count}</div>
                    <div style={S.sub}>pending for approval</div>
                </div>
            </div>

            <div style={S.placeholder}>
                <div style={S.placeholderIcon}>
                    {svg(26, (
                        <>
                            <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                        </>
                    ))}
                </div>
                <div style={S.placeholderTitle}>More KPIs</div>
                <div style={S.placeholderText}>Space reserved for more key performance metrics.</div>
            </div>
        </div>
    );
}

const S = {
    column: { minHeight: 0, display: "flex", flexDirection: "column", gap: 16, fontFamily: "'Poppins', sans-serif" },
    card: {
        flex: "none", width: "50%", minWidth: 130, minHeight: 150, borderRadius: 14,
        background: "linear-gradient(160deg,#6E58A6,#4C3B78)", display: "flex", flexDirection: "column",
        justifyContent: "space-between", padding: 16, color: "#fff", position: "relative", cursor: "pointer",
    },
    attentionPill: {
        alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 4,
        fontSize: 10.8, fontWeight: 700, letterSpacing: "0.04em", padding: "3px 8px", borderRadius: 999,
        background: "rgba(217,138,158,0.28)", color: "#F6D3DB",
    },
    iconBadge: { width: 26, height: 26, borderRadius: 8, background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" },
    metricLabel: { fontSize: 9.5, letterSpacing: "0.08em", fontWeight: 600, color: "#D8CCEE" },
    value: { fontFamily: "'Poppins'", fontSize: 30, fontWeight: 600, lineHeight: 1 },
    sub: { fontSize: 11, color: "#E0D6F2", fontWeight: 400, marginTop: 5, lineHeight: 1.3 },
    placeholder: {
        flex: 1, minHeight: 0, borderRadius: 16, border: "1.5px dashed #D6CCE8",
        background: "repeating-linear-gradient(135deg,#FCFBFE,#FCFBFE 14px,#F7F4FB 14px,#F7F4FB 28px)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 32,
    },
    placeholderIcon: { width: 52, height: 52, borderRadius: 15, background: "#EEE9F8", display: "flex", alignItems: "center", justifyContent: "center", color: "#8C6FC0", marginBottom: 16 },
    placeholderTitle: { fontFamily: "'Poppins'", fontSize: 18, fontWeight: 500, color: "#231C30" },
    placeholderText: { fontSize: 13, color: "#9A90AE", fontWeight: 300, marginTop: 6, maxWidth: 220, lineHeight: 1.5 },
};
